import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, Trash2, Search, Plus, Package, Eye, Settings, RefreshCw, CheckSquare, Square, FileCode, Download, Edit3, Zap, Cloud } from 'lucide-react';
import { API_BASE_URL } from '../config';
import MarkdownEditor from '../components/MarkdownEditor';

const Documents = () => {
  const { getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentProducts, setDocumentProducts] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [reprocessing, setReprocessing] = useState(false);
  // Product selector state for document association
  const [prodSelSearch, setProdSelSearch] = useState('');
  const [prodSelPage, setProdSelPage] = useState(1);
  const [prodSelPageSize, setProdSelPageSize] = useState(20);
  const [prodSelTotalCount, setProdSelTotalCount] = useState(0);
  const [prodSelHasNext, setProdSelHasNext] = useState(false);
  const [prodSelRows, setProdSelRows] = useState([]);
  const [reprocessingV2, setReprocessingV2] = useState(false);
  const [processingPipeline, setProcessingPipeline] = useState('v2'); // always v2
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('Confirm Action');
  const [confirmDetails, setConfirmDetails] = useState(null); // { rows: [[label,value], ...], note?: string }
  const [highlightedDocument, setHighlightedDocument] = useState(null);
  const [highlightedPage, setHighlightedPage] = useState(null);
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const [selectedMarkdownContent, setSelectedMarkdownContent] = useState('');
  const [selectedMarkdownFilename, setSelectedMarkdownFilename] = useState('');
  const [showMarkdownEditor, setShowMarkdownEditor] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentProducts, setDeleteDocumentProducts] = useState([]);
  const [vectorStoreUploading, setVectorStoreUploading] = useState(false);
  const [showVectorStoreModal, setShowVectorStoreModal] = useState(false);
  const [vectorStoreDocument, setVectorStoreDocument] = useState(null);
  const [vectorStoreProgress, setVectorStoreProgress] = useState(null);
  // Pagination state
  const [docPageSize, setDocPageSize] = useState(10);
  const [docPage, setDocPage] = useState(1);
  const [docTotal, setDocTotal] = useState(0);
  
  // Vector store configuration and bulk upload states
  const [availableVectorStores, setAvailableVectorStores] = useState([]);
  const [selectedVectorStore, setSelectedVectorStore] = useState('');
  const [defaultVectorStore, setDefaultVectorStore] = useState('');
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadType, setBulkUploadType] = useState('selected'); // 'all' or 'selected'
  const [bulkUploadProgress, setBulkUploadProgress] = useState(null);

  // Upload modal product selector state (server-side)
  const [upSelSearch, setUpSelSearch] = useState('');
  const [upSelPage, setUpSelPage] = useState(1);
  const [upSelPageSize, setUpSelPageSize] = useState(20);
  const [upSelTotalCount, setUpSelTotalCount] = useState(0);
  const [upSelHasNext, setUpSelHasNext] = useState(false);
  const [upSelRows, setUpSelRows] = useState([]);

  // Batch upload status
  const [batchInProgress, setBatchInProgress] = useState(false);
  const [batchResults, setBatchResults] = useState([]); // {filename,status,message,documentId?}
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Track initial load to prevent double calls
  const initialLoadRef = useRef(false);
  const fetchDocumentsRef = useRef();
  const fetchProductsRef = useRef();
  const fetchVectorStoresRef = useRef();
  const highlightDocumentRef = useRef();
  const searchTermRef = useRef('');
  const docPageSizeRef = useRef(docPageSize);
  const isInitialMountRef = useRef(true);

  const fetchDocuments = useCallback(async (page = 1, pageSize = 10, search = '') => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated
      
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString()
      });
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/?${params.toString()}`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        const docs = data.documents || [];
        
        // Debug: Check for duplicate IDs
        const ids = docs.map(d => d.id);
        const uniqueIds = [...new Set(ids)];
        if (ids.length !== uniqueIds.length) {
          console.warn('⚠️ Duplicate document IDs detected:', ids.filter((id, index) => ids.indexOf(id) !== index));
        }
        
        console.log(`📄 Fetched ${docs.length} documents with IDs:`, ids);
        setDocuments(docs);
        
        // Update pagination info if available from API response
        if (data.pagination) {
          setDocTotal(data.pagination.total_count || docs.length);
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Update refs when functions change
  fetchDocumentsRef.current = fetchDocuments;

  const fetchProducts = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/products/`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [getAuthHeaders]);

  // Update refs when functions change
  fetchProductsRef.current = fetchProducts;

  // Fetch available vector stores from configuration
  const fetchVectorStores = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        const stores = [];
        
        // Extract vector stores from configuration
        if (data.vector_stores) {
          data.vector_stores.forEach((store, index) => {
            stores.push({
              id: store.vector_store_id,
              name: store.name,
              description: store.description,
              assistant_name: store.assistant_name,
              store_index: index
            });
          });
        }
        
        setAvailableVectorStores(stores);
        
        // Set default vector store based on configuration
        if (data.default_vector_store_index !== undefined && data.vector_stores) {
          const defaultStore = data.vector_stores[data.default_vector_store_index];
          if (defaultStore) {
            setDefaultVectorStore(defaultStore.vector_store_id);
            setSelectedVectorStore(defaultStore.vector_store_id);
          }
        } else if (stores.length > 0) {
          // If no default is set, use the first available
          setDefaultVectorStore(stores[0].id);
          setSelectedVectorStore(stores[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching vector stores:', error);
    }
  }, [getAuthHeaders]);

  // Update refs when functions change
  fetchVectorStoresRef.current = fetchVectorStores;

  const highlightDocument = useCallback((documentId, page = null) => {
    setHighlightedDocument(documentId);
    setHighlightedPage(page);
    
    // Scroll to the document
    const documentElement = document.getElementById(`document-${documentId}`);
    if (documentElement) {
      documentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Add highlight effect
      documentElement.classList.add('highlight-pulse');
      setTimeout(() => {
        documentElement.classList.remove('highlight-pulse');
      }, 3000);
    }
    
    // Show page info if available
    if (page) {
      // You could show a toast or modal with page information
      console.log(`Highlighting document ${documentId} at page ${page}`);
    }
  }, []);

  // Update refs when functions change
  highlightDocumentRef.current = highlightDocument;
  searchTermRef.current = searchTerm;
  docPageSizeRef.current = docPageSize;

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      // If search term is empty, clear search instead
      handleClearSearch();
      return;
    }
    // Reset to page 1 when searching
    setDocPage(1);
    // Trigger fetch with current search term
    fetchDocumentsRef.current?.(1, docPageSize, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDocPage(1);
    // Fetch all documents without search
    fetchDocumentsRef.current?.(1, docPageSize, '');
  };

  // Initial load effect - only runs once on component mount
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      initialLoadRef.current = true;
      fetchDocumentsRef.current?.(1, docPageSizeRef.current, ''); // Initial load
      fetchProductsRef.current?.(); // Fetch products
      fetchVectorStoresRef.current?.(); // Fetch available vector stores
      
      // Check if we should highlight a specific document (from chat navigation)
      const highlightData = sessionStorage.getItem('highlightDocument');
      if (highlightData) {
        try {
          const { id, page } = JSON.parse(highlightData);
          // Clear the session storage
          sessionStorage.removeItem('highlightDocument');
          
          // Highlight the document after data is loaded
          setTimeout(() => {
            highlightDocumentRef.current?.(id, page);
          }, 1000);
        } catch (error) {
          console.error('Error parsing highlight data:', error);
        }
      }
    }
  }, []); // Empty dependency array - only runs once on mount

  // Pagination function - called directly from button clicks
  const handlePagination = (newPage) => {
    setDocPage(newPage);
    fetchDocumentsRef.current?.(newPage, docPageSize, searchTermRef.current);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) return;

    try {
      setUploading(true);
      setBatchInProgress(true);
      setBatchResults([]);
      setBatchProgress({ current: 0, total: selectedFiles.length });
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated

      for (let i = 0; i < selectedFiles.length; i++) {
        const f = selectedFiles[i];
        try {
          const formData = new FormData();
          formData.append('file', f);
          formData.append('product_codes', selectedProducts.join(',')); // optional
          if (description) formData.append('description', description);
          if (tags) formData.append('tags', tags);
          const isZip = (f.name || '').toLowerCase().endsWith('.zip');
          if (isZip) {
            formData.append('pipeline', 'v2');
          }
          const endpoint = isZip ? `${API_BASE_URL}/api/v1/documents/upload-zip` : `${API_BASE_URL}/api/v1/documents/upload`;
          const resp = await fetch(endpoint, {
            method: 'POST',
            headers: { 'X-API-Key': headers['X-API-Key'] },
            body: formData
          });
          if (!resp.ok) {
            const err = await resp.json().catch(()=>({detail:'Unknown error'}));
            setBatchResults(prev => [...prev, { filename: f.name, status: 'failed', message: err.detail || 'Upload failed' }]);
            setBatchProgress(prev => ({ ...prev, current: prev.current + 1 }));
            continue;
          }
          const uploadResult = await resp.json();
          if (isZip) {
            const zipResults = Array.isArray(uploadResult?.results) ? uploadResult.results : [];
            if (zipResults.length === 0) {
              setBatchResults(prev => [...prev, { filename: f.name, status: 'uploaded', message: 'ZIP processed (no files?)' }]);
            } else {
              setBatchResults(prev => [
                ...prev,
                ...zipResults.map(r => ({ filename: `${f.name} → ${r.filename}`, status: r.status, message: r.message, documentId: r.documentId }))
              ]);
            }
          } else {
			if (uploadResult && uploadResult.duplicate) {
              // Show confirmation to overwrite
              const existing = uploadResult.existing || {};
              setBatchResults(prev => [
                ...prev,
                { filename: f.name, status: 'duplicate', message: `Exists: ${existing.name} (${existing.size} bytes)` }
              ]);
              showConfirmation(
                `A document named "${existing.name || f.name}" already exists. Do you want to overwrite it with the new upload?`,
                async () => {
                  try {
                    const fd2 = new FormData();
                    fd2.append('file', f);
                    fd2.append('product_codes', selectedProducts.join(','));
                    if (description) fd2.append('description', description);
                    if (tags) fd2.append('tags', tags);
                    fd2.append('overwrite', 'true');
                    const resp2 = await fetch(`${API_BASE_URL}/api/v1/documents/upload`, {
                      method: 'POST',
                      headers: { 'X-API-Key': headers['X-API-Key'] },
                      body: fd2,
                    });
                    if (!resp2.ok) {
                      const err2 = await resp2.json().catch(()=>({detail:'Unknown error'}));
                      setBatchResults(prev => [
                        ...prev,
                        { filename: f.name, status: 'failed', message: `Overwrite failed: ${err2.detail || 'Unknown'}` }
                      ]);
                      return;
                    }
                    const over = await resp2.json();
                    const docIdOver = over?.document?.id;
                    setBatchResults(prev => [
                      ...prev,
                      { filename: f.name, status: 'uploaded', message: 'Overwritten successfully', documentId: docIdOver }
                    ]);
                    // Optionally trigger V2 processing after overwrite
                    if (docIdOver) {
                      try {
                        const v2Resp2 = await fetch(`${API_BASE_URL}/api/v1/documents/${docIdOver}/process-v2`, {
                          method: 'POST',
                          headers: { 'X-API-Key': headers['X-API-Key'] }
                        });
                        if (v2Resp2.ok) {
                          const v2b = await v2Resp2.json();
                          setBatchResults(prev => [
                            ...prev,
                            { filename: f.name, status: 'processed', message: `Pages: ${v2b.pages_processed}, type: ${v2b.processing_type}`, documentId: docIdOver }
                          ]);
                        } else {
                          const verr2 = await v2Resp2.json().catch(()=>({detail:'Unknown error'}));
                          setBatchResults(prev => [
                            ...prev,
                            { filename: f.name, status: 'uploaded', message: `V2 failed: ${verr2.detail || 'Unknown'}`, documentId: docIdOver }
                          ]);
                        }
                      } catch (ve2) {
                        setBatchResults(prev => [
                          ...prev,
                          { filename: f.name, status: 'uploaded', message: `V2 error: ${ve2.message}`, documentId: docIdOver }
                        ]);
                      }
                    }
                    // Refresh list
                    fetchDocuments();
                  } catch (ex) {
                    setBatchResults(prev => [
                      ...prev,
                      { filename: f.name, status: 'failed', message: `Overwrite error: ${ex.message}` }
                    ]);
                  }
                },
                {
                  title: 'Overwrite Existing Document',
                  details: {
                    rows: [
                      ['Existing name', existing.name || f.name],
                      ['Existing size', `${existing.size ?? ''} bytes`],
                      ['Existing uploaded', existing.uploaded_at || ''],
                      ['New file', f.name],
                      ['New size', `${f.size} bytes`],
                    ],
                    note: 'This will replace the existing file contents while keeping all product associations intact.'
                  }
                }
              );
              // Move to next file without marking progress now; progress will be updated after user action.
              continue;
            }
            let docId = uploadResult?.document?.id;
            // Optional V2 processing
            if (docId) {
              try {
                const v2Resp = await fetch(`${API_BASE_URL}/api/v1/documents/${docId}/process-v2`, {
                  method: 'POST',
                  headers: { 'X-API-Key': headers['X-API-Key'] }
                });
                if (!v2Resp.ok) {
                  const verr = await v2Resp.json().catch(()=>({detail:'Unknown error'}));
                  setBatchResults(prev => [...prev, { filename: f.name, status: 'uploaded', message: `V2 failed: ${verr.detail || 'Unknown'}`, documentId: docId }]);
                } else {
                  const v2 = await v2Resp.json();
                  setBatchResults(prev => [...prev, { filename: f.name, status: 'processed', message: `Pages: ${v2.pages_processed}, type: ${v2.processing_type}`, documentId: docId }]);
                }
              } catch (ve) {
                setBatchResults(prev => [...prev, { filename: f.name, status: 'uploaded', message: `V2 error: ${ve.message}`, documentId: docId }]);
              }
            } else {
              setBatchResults(prev => [...prev, { filename: f.name, status: 'uploaded', message: 'Uploaded', documentId: docId }]);
            }
          }
          setBatchProgress(prev => ({ ...prev, current: prev.current + 1 }));
        } catch (e) {
          setBatchResults(prev => [...prev, { filename: f.name, status: 'failed', message: e.message }]);
          setBatchProgress(prev => ({ ...prev, current: prev.current + 1 }));
        }
      }

      // Refresh documents after batch
      fetchDocuments(docPage, docPageSize, searchTerm);
    } catch (error) {
      console.error('Batch upload error:', error);
    } finally {
      setUploading(false);
      setBatchInProgress(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      // First, fetch the document details and associated products
      const headers = getAuthHeaders();
      if (!headers) return;
      
      // Get document details
      const docResponse = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}`, {
        headers: headers
      });
      
      if (!docResponse.ok) {
        alert('Failed to fetch document details');
        return;
      }
      
      const docData = await docResponse.json();
      
      // Get associated products
      const productsResponse = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}/products`, {
        headers: headers
      });
      
      let associatedProducts = [];
      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        associatedProducts = productsData.products || [];
      }
      
      // Set data for delete confirmation modal
      setDocumentToDelete(docData.document);
      setDeleteDocumentProducts(associatedProducts);
      setShowDeleteModal(true);
      
    } catch (error) {
      console.error('Error preparing document deletion:', error);
      alert('Failed to prepare document deletion. Please try again.');
    }
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: headers
      });

      if (response.ok) {
        alert(`Document "${documentToDelete.original_filename}" and its product associations have been deleted successfully.`);
        setShowDeleteModal(false);
        setDocumentToDelete(null);
        setDeleteDocumentProducts([]);
        fetchDocuments(docPage, docPageSize, searchTerm);
      } else {
        const errorData = await response.json();
        alert(`Failed to delete document: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const cancelDeleteDocument = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
    setDeleteDocumentProducts([]);
  };

  const handleVectorStoreUpload = async (document) => {
    setVectorStoreDocument(document);
    setShowVectorStoreModal(true);
  };

  const confirmVectorStoreUpload = async () => {
    if (!vectorStoreDocument) return;
    
    try {
      setVectorStoreUploading(true);
      setVectorStoreProgress({ status: 'preparing', message: 'Preparing document for upload...' });
      
      const headers = getAuthHeaders();
      if (!headers) return;
      
      setVectorStoreProgress({ status: 'uploading', message: 'Uploading to OpenAI vector store...' });
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${vectorStoreDocument.id}/upload-to-vector-store`, {
        method: 'POST',
        headers: headers
      });

      if (response.ok) {
        const result = await response.json();
        setVectorStoreProgress({ 
          status: 'completed', 
          message: `Document uploaded successfully to vector store: ${result.vector_store_id}`,
          result: result
        });
        
        // Refresh documents to show updated vector store status
        setTimeout(() => {
          fetchDocuments(docPage, docPageSize, searchTerm);
          setShowVectorStoreModal(false);
          setVectorStoreProgress(null);
        }, 2000);
      } else {
        const errorData = await response.json();
        setVectorStoreProgress({ 
          status: 'error', 
          message: `Upload failed: ${errorData.detail || 'Unknown error'}` 
        });
      }
    } catch (error) {
      console.error('Error uploading to vector store:', error);
      setVectorStoreProgress({ 
        status: 'error', 
        message: `Upload failed: ${error.message}` 
      });
    } finally {
      setVectorStoreUploading(false);
    }
  };

  const cancelVectorStoreUpload = () => {
    setShowVectorStoreModal(false);
    setVectorStoreDocument(null);
    setVectorStoreProgress(null);
  };

  // Bulk upload functions
  const handleBulkUpload = () => {
    if (bulkUploadType === 'selected' && selectedDocuments.length === 0) {
      alert('Please select documents to upload.');
      return;
    }
    if (availableVectorStores.length === 0) {
      alert('No vector stores available. Please configure vector stores first.');
      return;
    }
    setShowBulkUploadModal(true);
  };

  const confirmBulkUpload = async () => {
    if (!selectedVectorStore) {
      alert('Please select a vector store.');
      return;
    }

    const documentsToUpload = bulkUploadType === 'all' 
      ? documents.filter(doc => {
          const fileType = doc.file_type?.toLowerCase()?.trim() || '';
          const validTypes = ['pdf', 'docx', 'xlsx', '.pdf', '.docx', '.xlsx'];
          const validType = validTypes.some(type => 
            fileType === type || fileType.endsWith(type) || doc.original_filename?.toLowerCase().endsWith(type)
          );
          const vectorFileId = doc.vector_file_id;
          const notUploaded = !vectorFileId || vectorFileId === null || vectorFileId === '';
          return validType && notUploaded;
        })
      : selectedDocuments.map(docId => documents.find(d => d.id === docId)).filter(doc => {
          if (!doc) return false;
          const fileType = doc.file_type?.toLowerCase()?.trim() || '';
          const validTypes = ['pdf', 'docx', 'xlsx', '.pdf', '.docx', '.xlsx'];
          const validType = validTypes.some(type => 
            fileType === type || fileType.endsWith(type) || doc.original_filename?.toLowerCase().endsWith(type)
          );
          const vectorFileId = doc.vector_file_id;
          const notUploaded = !vectorFileId || vectorFileId === null || vectorFileId === '';
          
          console.log(`🚀 Upload Filter - ${doc.original_filename}:`);
          console.log(`  - file_type: "${doc.file_type}" (cleaned: "${fileType}")`);
          console.log(`  - validType: ${validType}`);
          console.log(`  - vector_file_id: "${vectorFileId}"`);
          console.log(`  - notUploaded: ${notUploaded}`);
          console.log(`  - eligible: ${validType && notUploaded}`);
          
          return validType && notUploaded;
        });

    if (documentsToUpload.length === 0) {
      alert('No eligible documents to upload. Documents must be PDF, DOCX, or XLSX format and not already uploaded to vector store.');
      return;
    }

    try {
      setVectorStoreUploading(true);
      setBulkUploadProgress({ 
        status: 'preparing', 
        message: `Preparing ${documentsToUpload.length} documents for batch upload...`,
        current: 0,
        total: documentsToUpload.length,
        completed: [],
        failed: []
      });

      const headers = getAuthHeaders();
      if (!headers) return;

      // Use batch upload endpoint
      const documentIds = documentsToUpload.map(doc => doc.id).join(',');
      const formData = new FormData();
      formData.append('document_ids', documentIds);
      formData.append('vector_store_id', selectedVectorStore);

      setBulkUploadProgress(prev => ({
        ...prev,
        status: 'uploading',
        message: `Uploading ${documentsToUpload.length} files using OpenAI batch upload...`
      }));

      const response = await fetch(`${API_BASE_URL}/api/v1/documents/batch-upload-to-vector-store`, {
        method: 'POST',
        headers: {
          'X-API-Key': headers['X-API-Key'],
          // Don't set Content-Type for FormData - browser will set multipart/form-data
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        
        setBulkUploadProgress({
          status: 'completed',
          message: result.message,
          current: result.uploaded_count,
          total: documentsToUpload.length,
          completed: result.successful_uploads || [],
          failed: result.skipped_documents || [],
          batch_id: result.batch_id,
          batch_status: result.batch_status
        });

        // Refresh documents to show updated vector store status
        setTimeout(() => {
          fetchDocuments(docPage, docPageSize, searchTerm);
        }, 2000);

      } else {
        const errorData = await response.json();
        setBulkUploadProgress({
          status: 'error',
          message: `Batch upload failed: ${errorData.detail || 'Unknown error'}`,
          current: 0,
          total: documentsToUpload.length,
          completed: [],
          failed: []
        });
      }

    } catch (error) {
      console.error('Error in batch upload:', error);
      setBulkUploadProgress({
        status: 'error',
        message: `Batch upload failed: ${error.message}`,
        current: 0,
        total: 0,
        completed: [],
        failed: []
      });
    } finally {
      setVectorStoreUploading(false);
    }
  };

  const cancelBulkUpload = () => {
    setShowBulkUploadModal(false);
    setBulkUploadProgress(null);
  };

  const handleProductToggle = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(p => p !== productCode)
        : [...prev, productCode]
    );
  };

  const handleManageProducts = async (document) => {
    setSelectedDocument(document);
    setShowProductModal(true);
    
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${document.id}/products`, {
        headers: headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocumentProducts(data.products || []);
        // Set current products as selected
        setSelectedProducts(data.products.map(p => p.product_code));
      }
      // Reset selector state and load first page
      setProdSelSearch('');
      setProdSelPage(1);
      await loadProductsSelector(document.id, 1, prodSelPageSize, '');
    } catch (error) {
      console.error('Error fetching document products:', error);
      setDocumentProducts([]);
      setSelectedProducts([]);
    }
  };

  const handleUnlinkProductFromDocument = async (productCode) => {
    if (!selectedDocument) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const newCodes = selectedProducts.filter((p) => p !== productCode);
      const resp = await fetch(`${API_BASE_URL}/api/v1/documents/${selectedDocument.id}/products`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_codes: newCodes })
      });
      if (resp.ok) {
        setSelectedProducts(newCodes);
        setDocumentProducts((prev) => prev.filter((p) => p.product_code !== productCode));
        // refresh selector list to update association flags
        await loadProductsSelector(selectedDocument.id, prodSelPage, prodSelPageSize, prodSelSearch);
      } else {
        const err = await resp.json().catch(()=>({detail:'Unknown error'}));
        alert(`Unlink failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error unlinking product:', e);
      alert('Failed to unlink product.');
    }
  };

  const loadProductsSelector = async (documentId, page, pageSize, search) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
        search: search || ''
      });
      const resp = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}/products/selector?${params.toString()}`, { headers });
      if (!resp.ok) return;
      const data = await resp.json();
      setProdSelRows(data.products || []);
      const p = data.pagination || {};
      setProdSelTotalCount(Number(p.total_count || 0));
      setProdSelHasNext(Boolean(p.has_next));
    } catch (e) {
      console.error('Selector fetch failed:', e);
      setProdSelRows([]);
      setProdSelTotalCount(0);
      setProdSelHasNext(false);
    }
  };

  useEffect(() => {
    if (showProductModal && selectedDocument) {
      loadProductsSelector(selectedDocument.id, prodSelPage, prodSelPageSize, prodSelSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodSelPage, prodSelPageSize, prodSelSearch]);

  const handleUpdateDocumentProducts = async () => {
    if (!selectedDocument) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${selectedDocument.id}/products`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_codes: selectedProducts
        })
      });

      if (response.ok) {
        setShowProductModal(false);
        setSelectedDocument(null);
        setSelectedProducts([]);
        setDocumentProducts([]);
        fetchDocuments(docPage, docPageSize, searchTerm);
        alert('Document products updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Update failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
        console.error('Error updating document products:', error);
        alert('Update failed. Please try again.');
    }
  };

  const handleDocumentSelection = (documentId) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAllDocuments = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  };

  const showConfirmation = (message, action, options = {}) => {
    setConfirmTitle(options.title || 'Confirm Action');
    setConfirmMessage(message);
    setConfirmDetails(options.details || null);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmMessage('');
  };

  const handleReprocessSelected = async () => {
    if (selectedDocuments.length === 0) {
      alert('Please select documents to reprocess');
      return;
    }

    showConfirmation(
      `Are you sure you want to reprocess ${selectedDocuments.length} document(s)? This will regenerate their text chunks and embeddings.`,
      async () => {
        try {
          setReprocessing(true);
          const headers = getAuthHeaders();
          if (!headers) return;
          
          const response = await fetch(`${API_BASE_URL}/api/v1/documents/reprocess/selected`, {
            method: 'POST',
            headers: {
              ...headers,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              document_ids: selectedDocuments
            })
          });

          if (response.ok) {
            alert('Documents reprocessed successfully!');
            setSelectedDocuments([]);
            fetchDocuments(docPage, docPageSize, searchTerm);
          } else {
            const errorData = await response.json();
            alert(`Reprocessing failed: ${errorData.detail || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error reprocessing documents:', error);
          alert('Reprocessing failed. Please try again.');
        } finally {
          setReprocessing(false);
        }
      }
    );
  };

  const handleViewMarkdown = async (documentId, filename) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}/markdown`, {
        headers: headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedMarkdownContent(data.content || 'No markdown content available');
        setSelectedMarkdownFilename(filename);
        setShowMarkdownModal(true);
      } else {
        alert('Failed to load markdown content');
      }
    } catch (error) {
      console.error('Error loading markdown:', error);
      alert('Failed to load markdown content');
    }
  };

  const handleDownloadMarkdown = async (documentId, filename) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}/markdown/download`, {
        headers: headers
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download markdown');
      }
    } catch (error) {
      console.error('Error downloading markdown:', error);
      alert('Failed to download markdown');
    }
  };

  const handleOpenMarkdownEditor = async (documentId, filename) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}/markdown`, {
        headers: headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setEditingDocument({
          id: documentId,
          filename: filename,
          content: data.content || 'No markdown content available'
        });
        setShowMarkdownEditor(true);
      } else {
        alert('Failed to load markdown content for editing');
      }
    } catch (error) {
      console.error('Error loading markdown for editing:', error);
      alert('Failed to load markdown content for editing');
    }
  };

  const handleSaveMarkdown = async (newContent) => {
    if (!editingDocument) return;
    
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      // Use the new PUT endpoint to update markdown content
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/${editingDocument.id}/markdown`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          markdown_content: newContent
        })
      });
      
      if (response.ok) {
        alert('Markdown content updated successfully!');
        
        // Update the local state
        setEditingDocument(prev => ({
          ...prev,
          content: newContent
        }));
        
        // Refresh the documents list to show updated content
        fetchDocuments(docPage, docPageSize, searchTerm);
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save markdown content');
      }
      
    } catch (error) {
      console.error('Error saving markdown:', error);
      throw new Error('Failed to save markdown content');
    }
  };

  const handleReprocessAll = async () => {
    showConfirmation(
      'Are you sure you want to reprocess ALL documents with V1 pipeline? This will regenerate their text chunks and embeddings for all documents in your account.',
      async () => {
        try {
          setReprocessing(true);
          const headers = getAuthHeaders();
          if (!headers) return;
          
          const response = await fetch(`${API_BASE_URL}/api/v1/documents/reprocess/all`, {
            method: 'POST',
            headers: headers
          });

          if (response.ok) {
            const result = await response.json();
            alert(`V1 Reprocessing completed successfully!\n\n${result.documents_processed} documents processed\n${result.failed_documents.length} documents failed`);
            fetchDocuments(docPage, docPageSize, searchTerm);
          } else {
            const errorData = await response.json();
            alert(`V1 Reprocessing failed: ${errorData.detail || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error reprocessing all documents (V1):', error);
          alert('V1 Reprocessing failed. Please try again.');
        } finally {
          setReprocessing(false);
        }
      }
    );
  };

  const handleReprocessAllV2 = async () => {
    showConfirmation(
      'Are you sure you want to reprocess ALL documents with V2 pipeline? This will convert documents to high-resolution images and use GPT-4o vision to generate enhanced markdown content. This process may take longer but provides better content quality.',
      async () => {
        try {
          setReprocessingV2(true);
          const headers = getAuthHeaders();
          if (!headers) return;
          
          const response = await fetch(`${API_BASE_URL}/api/v1/documents/reprocess-all-v2`, {
            method: 'POST',
            headers: headers
          });

          if (response.ok) {
            const result = await response.json();
            alert(`V2 Reprocessing completed successfully!\n\n${result.documents_processed} documents processed\n${result.documents_failed} documents failed\n\nTotal documents: ${result.total_documents}`);
            fetchDocuments(docPage, docPageSize, searchTerm);
          } else {
            const errorData = await response.json();
            alert(`V2 Reprocessing failed: ${errorData.detail || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error reprocessing all documents (V2):', error);
          alert('V2 Reprocessing failed. Please try again.');
        } finally {
          setReprocessingV2(false);
        }
      }
    );
  };

  const handleReprocessDocumentV2 = async (documentId, filename) => {
    showConfirmation(
      `Are you sure you want to reprocess "${filename}" with V2 pipeline? This will convert the document to high-resolution images and use GPT-4o vision to generate enhanced markdown content.`,
      async () => {
        try {
          setReprocessingV2(true);
          const headers = getAuthHeaders();
          if (!headers) return;
          
          const response = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}/process-v2`, {
            method: 'POST',
            headers: headers
          });

          if (response.ok) {
            const result = await response.json();
            alert(`V2 Processing completed successfully!\n\nDocument: ${result.filename}\nPages processed: ${result.pages_processed}\nContent length: ${result.content_length} characters\nProcessing type: ${result.processing_type}`);
            fetchDocuments(docPage, docPageSize, searchTerm);
          } else {
            const errorData = await response.json();
            alert(`V2 Processing failed: ${errorData.detail || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error processing document with V2:', error);
          alert('V2 Processing failed. Please try again.');
        } finally {
          setReprocessingV2(false);
        }
      }
    );
  };

  const resetUploadForm = () => {
    setSelectedFiles([]);
    setSelectedProducts([]);
    setDescription('');
    setTags('');
    setProcessingPipeline('v1');
    setShowUploadModal(false);
  };

  const loadUploadProductSelector = async (page, pageSize, search) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
        search: search || ''
      });
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/selector?${params.toString()}`, { headers });
      if (!resp.ok) return;
      const data = await resp.json();
      setUpSelRows(data.products || []);
      const p = data.pagination || {};
      setUpSelTotalCount(Number(p.total_count || 0));
      setUpSelHasNext(Boolean(p.has_next));
    } catch (e) {
      console.error('Upload selector fetch failed:', e);
      setUpSelRows([]);
      setUpSelTotalCount(0);
      setUpSelHasNext(false);
    }
  };

  useEffect(() => {
    if (showUploadModal) {
      loadUploadProductSelector(upSelPage, upSelPageSize, upSelSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upSelPage, upSelPageSize, upSelSearch, showUploadModal]);

  // Ensure the upload form is clean whenever the modal is closed
  useEffect(() => {
    if (!showUploadModal) {
      setSelectedFiles([]);
      setSelectedProducts([]);
      setDescription('');
      setTags('');
      setProcessingPipeline('v1');
      setBatchInProgress(false);
      setBatchResults([]);
      setBatchProgress({ current: 0, total: 0 });
      setUpSelSearch('');
      setUpSelPage(1);
      setUpSelRows([]);
      setUpSelTotalCount(0);
      setUpSelHasNext(false);
    } else {
      // When opening, also reset transient progress state
      setBatchInProgress(false);
      setBatchResults([]);
      setBatchProgress({ current: 0, total: 0 });
    }
  }, [showUploadModal]);



  // For server-side pagination, we use the documents directly
  const paginatedDocuments = documents;
  const docTotalPages = Math.max(1, Math.ceil(docTotal / docPageSize));
  const docStart = (docPage - 1) * docPageSize + 1;
  const docEnd = Math.min(docTotal, docStart + docPageSize - 1);
  
  // Debug: Check documents for duplicates
  if (documents.length > 0) {
    const docIds = documents.map(d => d.id);
    const uniqueDocIds = [...new Set(docIds)];
    if (docIds.length !== uniqueDocIds.length) {
      console.warn('⚠️ Duplicate IDs in documents:', docIds.filter((id, index) => docIds.indexOf(id) !== index));
    }
    console.log(`🔍 Loaded ${documents.length} documents with IDs:`, docIds);
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
                      <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Documents</h2>
                <p className="text-muted mb-0">Manage and search your uploaded documents</p>
              </div>
              <div className="d-flex gap-2">
                {selectedDocuments.length > 0 && (
                  <button
                    className="btn btn-warning"
                    onClick={handleReprocessSelected}
                    disabled={reprocessing || reprocessingV2}
                  >
                    <RefreshCw size={20} className="me-2" />
                    {reprocessing ? 'Processing...' : `Reprocess Selected (${selectedDocuments.length})`}
                  </button>
                )}
                {/* <button
                  className="btn btn-info"
                  onClick={handleReprocessAll}
                  disabled={reprocessing || reprocessingV2}
                  title="V1 Pipeline: Standard text processing and chunking"
                >
                  <RefreshCw size={20} className="me-2" />
                  {reprocessing ? 'Processing V1...' : 'Reprocess All (V1)'}
                </button> */}
                <button
                  className="btn btn-success"
                  onClick={handleReprocessAllV2}
                  disabled={reprocessing || reprocessingV2}
                  title="V2 Pipeline: High-res image conversion + Vision Ai for enhanced processing"
                >
                  <RefreshCw size={20} className="me-2" />
                  {reprocessingV2 ? 'Processing...' : 'Reprocess All'}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowUploadModal(true)}
                  disabled={reprocessing || reprocessingV2}
                >
                  <Plus size={20} className="me-2" />
                  Upload Document
                </button>
                
                {/* Bulk Vector Store Upload Button */}
                {availableVectorStores.length > 0 && (
                  <button
                    className="btn btn-success"
                    onClick={handleBulkUpload}
                    disabled={vectorStoreUploading || (bulkUploadType === 'selected' && selectedDocuments.length === 0)}
                    title="Upload documents to vector store"
                  >
                    <Cloud size={20} className="me-2" />
                    Upload to Vector Store
                    {bulkUploadType === 'selected' && selectedDocuments.length > 0 && (
                      <span className="badge bg-light text-dark ms-2">{selectedDocuments.length}</span>
                    )}
                  </button>
                )}
              </div>
            </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <Search size={20} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search documents..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                    />
                    <button
                      className="btn btn-outline-primary"
                      type="button"
                      onClick={handleSearch}
                      title="Search documents"
                    >
                      <Search size={16} />
                    </button>
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleClearSearch}
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  {/* Upload Type Selection */}
                  {availableVectorStores.length > 0 && (
                    <div className="btn-group btn-group-sm me-3">
                      <input
                        type="radio"
                        className="btn-check"
                        name="uploadType"
                        id="upload-selected"
                        checked={bulkUploadType === 'selected'}
                        onChange={() => setBulkUploadType('selected')}
                      />
                      <label className="btn btn-outline-info" htmlFor="upload-selected">
                        Selected Files ({selectedDocuments.length})
                      </label>
                      
                      <input
                        type="radio"
                        className="btn-check"
                        name="uploadType"
                        id="upload-all"
                        checked={bulkUploadType === 'all'}
                        onChange={() => setBulkUploadType('all')}
                      />
                      <label className="btn btn-outline-info" htmlFor="upload-all">
                        All Files ({documents.filter(doc => ['pdf', 'docx', 'xlsx'].includes(doc.file_type?.toLowerCase()) && !doc.vector_file_id).length})
                      </label>
                    </div>
                  )}
                  
                  <div className="d-inline-flex align-items-center gap-2 me-3">
                    <label className="form-label mb-0">Per page:</label>
                    <select className="form-select form-select-sm" style={{ width: 90 }} value={docPageSize} onChange={(e)=>{
                      const newPageSize = Number(e.target.value);
                      setDocPageSize(newPageSize);
                      setDocPage(1);
                      fetchDocumentsRef.current?.(1, newPageSize, searchTermRef.current);
                    }}>
                      {[10,20,30].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <span className="text-muted me-3">
                    Showing {docTotal === 0 ? 0 : docStart}–{docEnd} of {docTotal}
                    {searchTerm && (
                      <span className="ms-2">
                        <span className="badge bg-info">Search: "{searchTerm}"</span>
                      </span>
                    )}
                  </span>
                  <div className="btn-group btn-group-sm" role="group">
                    <button className="btn btn-outline-secondary" disabled={docPage<=1} onClick={()=>handlePagination(Math.max(1, docPage-1))}>Prev</button>
                    <button className="btn btn-outline-secondary" disabled={docPage>=docTotalPages} onClick={()=>handlePagination(Math.min(docTotalPages, docPage+1))}>Next</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-body">
              {reprocessing ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-info" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <p className="mt-3">Reprocessing documents...</p>
                  <p className="text-muted">This may take a few minutes depending on the number of documents</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-5">
                  <FileText size={64} className="text-muted mb-3" />
                  <h5>No documents found</h5>
                  <p className="text-muted">Upload your first document to get started</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <Upload size={20} className="me-2" />
                    Upload Document
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleSelectAllDocuments}
                            title="Select/Deselect All"
                          >
                            {selectedDocuments.length === documents.length ? (
                              <CheckSquare size={16} />
                            ) : (
                              <Square size={16} />
                            )}
                          </button>
                        </th>
                        <th>Document</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Products</th>
                        <th>Content Length</th>
                        <th>Markdown Preview</th>
                        <th>Uploaded</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedDocuments.map((doc, index) => {
                        // Debug logging to help identify duplicate keys
                        console.log(`Rendering document row ${index}: ID=${doc.id}, Filename=${doc.original_filename || doc.filename}`);
                        return (
                        <tr 
                          key={`doc-${doc.id}-${index}`}
                          id={`document-${doc.id}`}
                          className={`document-card ${highlightedDocument === doc.id ? 'highlight-pulse' : ''}`}
                        >
                          <td>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleDocumentSelection(doc.id)}
                              title={selectedDocuments.includes(doc.id) ? "Deselect" : "Select"}
                            >
                              {selectedDocuments.includes(doc.id) ? (
                                <CheckSquare size={16} />
                              ) : (
                                <Square size={16} />
                              )}
                            </button>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <FileText size={20} className="text-primary me-2" />
                              <div>
                                <div className="fw-bold">{doc.original_filename || doc.filename}</div>
                                {doc.description && (
                                  <small className="text-muted">{doc.description}</small>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-secondary">
                              {doc.file_type?.toUpperCase() || 'UNKNOWN'}
                            </span>
                          </td>
                          <td>{doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}</td>
                          <td>
                            {doc.product_codes && doc.product_codes !== 'No products' ? (
                              <div className="d-flex flex-wrap gap-1">
                                {doc.product_codes.split(', ').map((code, index) => (
                                  <span key={`${doc.id}-product-${index}`} className="badge bg-info">
                                    <Package size={12} className="me-1" />
                                    {code}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">No products</span>
                            )}
                          </td>
                          <td>
                            {doc.content ? `${doc.content.length.toLocaleString()} chars` : 'N/A'}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info"
                              title="View Markdown"
                              onClick={() => handleViewMarkdown(doc.id, doc.original_filename || doc.filename)}
                            >
                              <FileCode size={16} />
                            </button>
                          </td>
                          <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                title="View Document"
                                onClick={() => alert(`Document ID: ${doc.id}\nContent preview: ${doc.content?.substring(0, 100)}...`)}
                              >
                                <Eye size={16} />
                              </button>
                              {doc.content && doc.content.trim() !== '' && (
                                <button
                                  className="btn btn-outline-success"
                                  title="Edit Markdown"
                                  onClick={() => handleOpenMarkdownEditor(doc.id, doc.original_filename || doc.filename)}
                                >
                                  <Edit3 size={16} />
                                </button>
                              )}
                              <button
                                className="btn btn-outline-warning"
                                title="Process with V2 Pipeline (High-res + GPT Vision)"
                                onClick={() => handleReprocessDocumentV2(doc.id, doc.original_filename || doc.filename)}
                                disabled={reprocessing || reprocessingV2}
                              >
                                <Zap size={16} />
                              </button>
                              <button
                                className="btn btn-outline-info"
                                title="Manage Products"
                                onClick={() => handleManageProducts(doc)}
                              >
                                <Settings size={16} />
                              </button>
                              {/* Vector Store Upload Button - only show for supported file types */}
                              {['pdf', 'docx', 'txt', 'md', '.pdf', '.docx', '.txt', '.md'].includes(doc.file_type?.toLowerCase()) && !doc.vector_file_id && (
                                <button
                                  className="btn btn-outline-primary"
                                  title="Upload to OpenAI Vector Store"
                                  onClick={() => handleVectorStoreUpload(doc)}
                                  disabled={vectorStoreUploading}
                                >
                                  <Cloud size={16} />
                                </button>
                              )}
                              {/* Show vector store status if uploaded */}
                              {doc.vector_file_id && (
                                <button
                                  className="btn btn-success"
                                  title="Already uploaded to vector store"
                                  disabled
                                >
                                  <Cloud size={16} />
                                </button>
                              )}
                              <button
                                className="btn btn-outline-danger"
                                title="Delete Document"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">Page {docPage} of {docTotalPages}</div>
                    <div className="btn-group btn-group-sm" role="group">
                      <button className="btn btn-outline-secondary" disabled={docPage<=1} onClick={()=>handlePagination(Math.max(1, docPage-1))}>Prev</button>
                      <button className="btn btn-outline-secondary" disabled={docPage>=docTotalPages} onClick={()=>handlePagination(Math.min(docTotalPages, docPage+1))}>Next</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload Document</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetUploadForm}
                ></button>
              </div>
              <form onSubmit={handleFileUpload}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Select File *</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf,.docx,.xlsx,.txt,.zip"
                          multiple
                          onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                        />
                        <div className="form-text">
                          Supported formats: PDF, DOCX, XLSX, TXT, ZIP (Max per file: 50MB)
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Products (optional)</label>
                        <div className="row g-2 align-items-center mb-2">
                          <div className="col"><input className="form-control form-control-sm" placeholder="Search products" value={upSelSearch} onChange={(e)=>{setUpSelSearch(e.target.value); setUpSelPage(1);}} /></div>
                          <div className="col-auto"><small className="text-muted">Per page</small></div>
                          <div className="col-auto">
                            <select className="form-select form-select-sm" value={upSelPageSize} onChange={(e)=>{setUpSelPageSize(Number(e.target.value)); setUpSelPage(1);}}>
                              {[10,20,30].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {upSelRows.length === 0 ? (
                            <p className="text-muted">No products found</p>
                          ) : (
                            upSelRows.map((product) => (
                              <div key={product.product_code} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`product-${product.product_code}`}
                                  checked={selectedProducts.includes(product.product_code)}
                                  onChange={() => handleProductToggle(product.product_code)}
                                />
                                <label className="form-check-label" htmlFor={`product-${product.product_code}`}>
                                  <strong>{product.product_code}</strong> - {product.name}
                                  {product.description && (
                                    <small className="text-muted d-block"> {product.description}</small>
                                  )}
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <div className="small text-muted">Page {upSelPage} of {Math.max(1, Math.ceil(upSelTotalCount/Math.max(1, upSelPageSize)))}</div>
                          <div className="btn-group btn-group-sm" role="group">
                            <button className="btn btn-outline-secondary" disabled={upSelPage<=1} onClick={()=>setUpSelPage(p=>Math.max(1,p-1))}>Prev</button>
                            <button className="btn btn-outline-secondary" disabled={!upSelHasNext} onClick={()=>setUpSelPage(p=>p+1)}>Next</button>
                          </div>
                        </div>
                        <div className="form-text">
                          You can associate products now or later from the document’s Manage Products.
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Optional description of the document"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Tags</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Optional tags (comma-separated)"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                        />
                        <div className="form-text">
                          Separate multiple tags with commas
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Vision processing is always used; radio removed */}
                  <div className="row">
                    <div className="col-12">
                      <div className="alert alert-info">
                        Vision Processing is enabled by default for higher-quality results.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetUploadForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`btn btn-success`}
                    disabled={(selectedFiles.length === 0) || uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {'Uploading & Processing with Vision...'}
                      </>
                    ) : (
                      <>
                        <Upload size={20} className="me-2" />
                        {'Upload & Process with Vision'}
                      </>
                    )}
                  </button>
                </div>
              </form>
              {/* Batch results */}
              {(batchResults.length > 0 || batchInProgress) && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="small text-muted">Progress: {batchProgress.current} / {batchProgress.total}</div>
                    <div className="progress" style={{ width: '200px', height: '6px' }}>
                      <div className="progress-bar" style={{ width: `${batchProgress.total ? (batchProgress.current / batchProgress.total) * 100 : 0}%` }} />
                    </div>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        // Generate CSV report
                        const headers = ['filename','status','message','documentId'];
                        const lines = [headers.join(',')].concat(
                          batchResults.map(r => [r.filename, r.status, (r.message||'').replace(/,/g,';'), r.documentId||''].join(','))
                        );
                        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `upload_batch_${Date.now()}.csv`; document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                      }}
                      disabled={batchResults.length === 0}
                    >
                      Download Report
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>File</th>
                          <th>Status</th>
                          <th>Message</th>
                          <th>ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchResults.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.filename}</td>
                            <td><span className={`badge ${r.status==='failed'?'bg-danger':(r.status==='processed'?'bg-success':'bg-info')}`}>{r.status}</span></td>
                            <td className="small">{r.message}</td>
                            <td>{r.documentId || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Management Modal */}
      {showProductModal && selectedDocument && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Manage Products for: {selectedDocument.original_filename || selectedDocument.filename}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedDocument(null);
                    setSelectedProducts([]);
                    setDocumentProducts([]);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Current Products</label>
                  <div className="border rounded p-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {documentProducts.length === 0 ? (
                      <p className="text-muted">No products currently associated</p>
                    ) : (
                      documentProducts.map((product) => (
                        <span key={product.product_code} className="badge bg-info me-2 mb-2 d-inline-flex align-items-center">
                          <Package size={12} className="me-1" />
                          <span>{product.product_code} - {product.name}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-light ms-2"
                            title="Unlink product"
                          onClick={() => showConfirmation(
                            `Are you sure you want to unlink this product from the document?`,
                            () => handleUnlinkProductFromDocument(product.product_code),
                            {
                              title: 'Unlink Product from Document',
                              details: {
                                rows: [
                                  ['Document', selectedDocument?.original_filename || selectedDocument?.filename || 'Unknown'],
                                  ['Product', `${product.product_code} — ${product.name || ''}`.trim()]
                                ],
                                note: 'This will remove the association only. The document and product will remain intact.'
                              }
                            }
                          )}
                          >
                            Unlink
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Select Products to Associate</label>
                  <div className="row g-2 align-items-center mb-2">
                    <div className="col-auto"><small className="text-muted">Search</small></div>
                    <div className="col"><input className="form-control form-control-sm" value={prodSelSearch} onChange={(e)=>{setProdSelSearch(e.target.value); setProdSelPage(1);}} placeholder="Search code or name" /></div>
                    <div className="col-auto"><small className="text-muted">Per page</small></div>
                    <div className="col-auto">
                      <select className="form-select form-select-sm" value={prodSelPageSize} onChange={(e)=>{setProdSelPageSize(Number(e.target.value)); setProdSelPage(1);}}>
                        {[10,20,30].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="border rounded p-3" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                    {prodSelRows.length === 0 ? (
                      <p className="text-muted">No products found</p>
                    ) : (
                      prodSelRows.map((product) => (
                        <div key={product.product_code} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`modal-product-${product.product_code}`}
                            checked={selectedProducts.includes(product.product_code)}
                            onChange={() => handleProductToggle(product.product_code)}
                          />
                          <label className="form-check-label" htmlFor={`modal-product-${product.product_code}`}>
                            <strong>{product.product_code}</strong> - {product.name} {product.is_associated ? '• Associated' : ''}
                            {product.description && (
                              <small className="text-muted d-block"> {product.description}</small>
                            )}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">Page {prodSelPage} of {Math.max(1, Math.ceil(prodSelTotalCount/Math.max(1, prodSelPageSize)))}</div>
                    <div className="btn-group btn-group-sm" role="group">
                      <button className="btn btn-outline-secondary" disabled={prodSelPage<=1} onClick={()=>setProdSelPage(p=>Math.max(1, p-1))}>Prev</button>
                      <button className="btn btn-outline-secondary" disabled={!prodSelHasNext} onClick={()=>setProdSelPage(p=>p+1)}>Next</button>
                    </div>
                  </div>
                  <div className="form-text">
                    Check/uncheck products to associate with this document
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedDocument(null);
                    setSelectedProducts([]);
                    setDocumentProducts([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateDocumentProducts}
                >
                  Update Products
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{confirmTitle}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmModal(false)}
                />
              </div>
              <div className="modal-body">
                <p className="mb-3">{confirmMessage}</p>
                {confirmDetails?.rows && (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-2">
                      <tbody>
                        {confirmDetails.rows.map(([label, value], idx) => (
                          <tr key={idx}>
                            <th style={{width: '140px'}} className="text-muted">{label}</th>
                            <td>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {confirmDetails?.note && (
                  <div className="alert alert-warning py-2 mb-0">
                    {confirmDetails.note}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleConfirmAction}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Markdown Preview Modal */}
      {showMarkdownModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FileCode size={20} className="me-2" />
                  Markdown Preview: {selectedMarkdownFilename}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMarkdownModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted">
                    Content Length: {selectedMarkdownContent.length.toLocaleString()} characters
                  </span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleDownloadMarkdown(
                      documents.find(d => d.original_filename === selectedMarkdownFilename)?.id,
                      selectedMarkdownFilename
                    )}
                  >
                    <Download size={16} className="me-1" />
                    Download MD
                  </button>
                </div>
                <div className="border rounded p-3 bg-light" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selectedMarkdownContent}
                  </pre>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMarkdownModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Markdown Editor Modal */}
      {showMarkdownEditor && editingDocument && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-fullscreen">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Edit3 size={20} className="me-2" />
                  Markdown Editor: {editingDocument.filename}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowMarkdownEditor(false);
                    setEditingDocument(null);
                  }}
                />
              </div>
              <div className="modal-body p-0">
                <MarkdownEditor
                  content={editingDocument.content}
                  filename={editingDocument.filename}
                  onSave={handleSaveMarkdown}
                  onClose={() => {
                    setShowMarkdownEditor(false);
                    setEditingDocument(null);
                  }}
                  readOnly={false}
                  showToolbar={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && documentToDelete && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <Trash2 size={20} className="me-2" />
                  Confirm Document Deletion
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelDeleteDocument}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-4">
                  <strong>⚠️ Warning:</strong> This action cannot be undone. The document and all its product associations will be permanently deleted.
                </div>
                
                <div className="mb-4">
                  <h6 className="mb-3">Document to be deleted:</h6>
                  <div className="card">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Filename:</strong> {documentToDelete.original_filename}
                        </div>
                        <div className="col-md-6">
                          <strong>File Type:</strong> {documentToDelete.file_type}
                        </div>
                        <div className="col-md-6">
                          <strong>File Size:</strong> {documentToDelete.file_size ? `${(documentToDelete.file_size / 1024).toFixed(1)} KB` : 'N/A'}
                        </div>
                        <div className="col-md-6">
                          <strong>Uploaded:</strong> {documentToDelete.uploaded_at ? new Date(documentToDelete.uploaded_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      {documentToDelete.description && (
                        <div className="mt-2">
                          <strong>Description:</strong> {documentToDelete.description}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {deleteDocumentProducts.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">Associated Products ({deleteDocumentProducts.length}):</h6>
                    <div className="alert alert-info">
                      <strong>📋 Note:</strong> Deleting this document will also remove its associations with the following products:
                    </div>
                    <div className="row">
                      {deleteDocumentProducts.map((product) => (
                        <div key={product.product_code} className="col-md-6 mb-2">
                          <div className="card border-warning">
                            <div className="card-body p-2">
                              <div className="d-flex align-items-center">
                                <Package size={16} className="text-warning me-2" />
                                <div>
                                  <strong>{product.product_code}</strong>
                                  <div className="small text-muted">{product.name}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {deleteDocumentProducts.length === 0 && (
                  <div className="mb-4">
                    <div className="alert alert-secondary">
                      <strong>📋 Note:</strong> This document has no product associations.
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <p className="text-muted mb-0">
                    Are you sure you want to permanently delete this document{deleteDocumentProducts.length > 0 ? ' and its product associations' : ''}?
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDeleteDocument}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteDocument}
                >
                  <Trash2 size={16} className="me-2" />
                  Delete Document{deleteDocumentProducts.length > 0 ? ' & Associations' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vector Store Upload Modal */}
      {showVectorStoreModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Cloud size={20} className="me-2" />
                  Upload to OpenAI Vector Store
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelVectorStoreUpload}
                  disabled={vectorStoreUploading}
                ></button>
              </div>
              <div className="modal-body">
                {vectorStoreDocument && (
                  <div className="mb-4">
                    <h6 className="mb-3">Document Details:</h6>
                    <div className="card bg-light">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <strong>Filename:</strong> {vectorStoreDocument.original_filename || vectorStoreDocument.filename}
                          </div>
                          <div className="col-md-6">
                            <strong>Type:</strong> {vectorStoreDocument.file_type?.toUpperCase() || 'UNKNOWN'}
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-md-6">
                            <strong>Size:</strong> {vectorStoreDocument.file_size ? `${(vectorStoreDocument.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                          </div>
                          <div className="col-md-6">
                            <strong>Vector Store ID:</strong> extremeb2b-{/* account_id will be dynamic */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!vectorStoreProgress && (
                  <div className="alert alert-info">
                    <strong>📋 About Vector Store Upload:</strong>
                    <ul className="mb-0 mt-2">
                      <li>This will upload your document to OpenAI's vector store for enhanced AI search capabilities</li>
                      <li>Supported file types: PDF, DOCX, TXT, MD</li>
                      <li>The document will be processed and indexed for semantic search</li>
                      <li>This process may take a few minutes depending on document size</li>
                    </ul>
                  </div>
                )}

                {vectorStoreProgress && (
                  <div className="mb-4">
                    <h6 className="mb-3">Upload Progress:</h6>
                    <div className={`alert ${
                      vectorStoreProgress.status === 'completed' ? 'alert-success' :
                      vectorStoreProgress.status === 'error' ? 'alert-danger' :
                      'alert-info'
                    }`}>
                      <div className="d-flex align-items-center">
                        {vectorStoreProgress.status === 'preparing' && <RefreshCw size={16} className="me-2 spin" />}
                        {vectorStoreProgress.status === 'uploading' && <Upload size={16} className="me-2" />}
                        {vectorStoreProgress.status === 'completed' && <CheckSquare size={16} className="me-2" />}
                        {vectorStoreProgress.status === 'error' && <Trash2 size={16} className="me-2" />}
                        <div>
                          <strong>{vectorStoreProgress.message}</strong>
                          {vectorStoreProgress.result && (
                            <div className="mt-2 small">
                              <div>Vector File ID: {vectorStoreProgress.result.vector_file_id}</div>
                              <div>File Size: {vectorStoreProgress.result.bytes ? `${(vectorStoreProgress.result.bytes / 1024).toFixed(0)} KB` : 'N/A'}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!vectorStoreProgress && (
                  <div className="text-center">
                    <p className="text-muted">
                      Ready to upload "{vectorStoreDocument?.original_filename || vectorStoreDocument?.filename}" to the vector store?
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelVectorStoreUpload}
                  disabled={vectorStoreUploading}
                >
                  {vectorStoreProgress?.status === 'completed' ? 'Close' : 'Cancel'}
                </button>
                {!vectorStoreProgress && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmVectorStoreUpload}
                    disabled={vectorStoreUploading}
                  >
                    <Cloud size={16} className="me-2" />
                    Upload to Vector Store
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Vector Store Upload Modal */}
      {showBulkUploadModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Cloud size={20} className="me-2" />
                  Bulk Upload to Vector Store
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelBulkUpload}
                  disabled={vectorStoreUploading}
                ></button>
              </div>
              <div className="modal-body">
                {!bulkUploadProgress && (
                  <>
                    {/* Vector Store Selection */}
                    <div className="mb-3">
                      <label htmlFor="vectorStoreSelect" className="form-label fw-semibold">
                        🏪 Select Vector Store
                      </label>
                      <select 
                        id="vectorStoreSelect"
                        className="form-select"
                        value={selectedVectorStore}
                        onChange={(e) => setSelectedVectorStore(e.target.value)}
                      >
                        {availableVectorStores.map((store) => (
                          <option key={store.id} value={store.id}>
                            {store.name}
                            {store.id === defaultVectorStore && ' - Default'}
                            {store.assistant_name && ` (${store.assistant_name})`}
                          </option>
                        ))}
                      </select>
                      {selectedVectorStore && (
                        <div className="form-text">
                          {availableVectorStores.find(s => s.id === selectedVectorStore)?.description || 'No description provided'}
                        </div>
                      )}
                    </div>

                    {/* Upload Type and File Count */}
                    <div className="alert alert-info">
                      <div className="fw-semibold">Upload Configuration:</div>
                      <div>
                        <strong>Type:</strong> {bulkUploadType === 'all' ? 'All eligible files' : 'Selected files only'}
                      </div>
                      <div>
                        <strong>Files to upload:</strong> {
                          (() => {
                            let count = 0;
                            let debugInfo = {};
                            
                            if (bulkUploadType === 'all') {
                              const eligible = documents.filter(doc => 
                                ['pdf', 'docx', 'xlsx'].includes(doc.file_type?.toLowerCase()) && !doc.vector_file_id
                              );
                              count = eligible.length;
                              debugInfo = {
                                mode: 'all',
                                totalDocs: documents.length,
                                eligibleDocs: count,
                                eligibleFiles: eligible.map(d => `${d.original_filename} (${d.file_type})`)
                              };
                            } else {
                              debugInfo = {
                                mode: 'selected',
                                selectedCount: selectedDocuments.length,
                                selectedIds: selectedDocuments,
                                totalDocs: documents.length
                              };
                              
                              if (selectedDocuments.length === 0) {
                                console.warn('🚨 NO DOCUMENTS SELECTED! Please check some checkboxes first.');
                                debugInfo.issue = 'No documents selected';
                              } else {
                                const mapped = selectedDocuments.map(docId => {
                                  const doc = documents.find(d => d.id === docId);
                                  if (!doc) {
                                    console.warn(`🚨 Document ID ${docId} not found in documents array!`);
                                  }
                                  return doc;
                                }).filter(Boolean);
                                
                                const eligible = mapped.filter(doc => {
                                  // Handle file type more robustly
                                  const fileType = doc.file_type?.toLowerCase()?.trim() || '';
                                  const validTypes = ['pdf', 'docx', 'xlsx', '.pdf', '.docx', '.xlsx'];
                                  const validType = validTypes.some(type => 
                                    fileType === type || fileType.endsWith(type) || doc.original_filename?.toLowerCase().endsWith(type)
                                  );
                                  
                                  // Handle vector_file_id more robustly
                                  const vectorFileId = doc.vector_file_id;
                                  const notUploaded = !vectorFileId || vectorFileId === null || vectorFileId === '';
                                  
                                  console.log(`🔍 ${doc.original_filename}:`);
                                  console.log(`  - file_type: "${doc.file_type}" (cleaned: "${fileType}")`);
                                  console.log(`  - filename: "${doc.original_filename}"`);
                                  console.log(`  - validType: ${validType} (checked: ${validTypes.join(', ')})`);
                                  console.log(`  - vector_file_id: "${vectorFileId}"`);
                                  console.log(`  - notUploaded: ${notUploaded}`);
                                  console.log(`  - eligible: ${validType && notUploaded}`);
                                  
                                  if (!validType) {
                                    console.warn(`❌ ${doc.original_filename}: Invalid type "${doc.file_type}" - must be PDF, DOCX, or XLSX`);
                                  }
                                  if (!notUploaded) {
                                    console.warn(`❌ ${doc.original_filename}: Already uploaded (vector_file_id: ${vectorFileId})`);
                                  }
                                  
                                  return validType && notUploaded;
                                });
                                
                                count = eligible.length;
                                debugInfo.mappedCount = mapped.length;
                                debugInfo.eligibleCount = count;
                                debugInfo.eligibleFiles = eligible.map(d => `${d.original_filename} (${d.file_type})`);
                                debugInfo.mappedFiles = mapped.map(d => `${d.original_filename} (${d.file_type}, vector_id: ${d.vector_file_id || 'none'})`);
                              }
                            }
                            
                            console.log('📊 UPLOAD COUNT DEBUG:', debugInfo);
                            return count;
                          })()
                        } files
                      </div>
                      
                      {/* Debug Panel */}
                      <div className="mt-2">
                        <small className="text-muted">
                          Debug: {selectedDocuments.length} selected, {documents.length} total docs
                          {bulkUploadType === 'selected' && selectedDocuments.length === 0 && (
                            <span className="text-danger ms-2">⚠️ No documents selected - check some boxes first!</span>
                          )}
                          {bulkUploadType === 'selected' && selectedDocuments.length > 0 && (
                            (() => {
                              const selectedFiles = selectedDocuments.map(id => {
                                const doc = documents.find(d => d.id === id);
                                return doc ? `${doc.original_filename} (${doc.file_type})` : `ID:${id} (not found)`;
                              });
                              return (
                                <div className="mt-1">
                                  <strong>Selected files:</strong> {selectedFiles.join(', ')}
                                </div>
                              );
                            })()
                          )}
                        </small>
                      </div>
                    </div>

                    <div className="alert alert-warning">
                      <strong>📋 About Bulk Vector Store Upload:</strong>
                      <ul className="mb-0 mt-2">
                        <li>Uses OpenAI's efficient batch upload endpoint</li>
                        <li>Only PDF, DOCX, and XLSX files will be uploaded</li>
                        <li>Files already uploaded to vector store will be skipped</li>
                        <li>Much faster than individual uploads for multiple files</li>
                        <li>Batch processing status will be displayed</li>
                      </ul>
                    </div>
                  </>
                )}

                {bulkUploadProgress && (
                  <div className={`alert ${
                    bulkUploadProgress.status === 'completed' ? 'alert-success' :
                    bulkUploadProgress.status === 'error' ? 'alert-danger' :
                    'alert-info'
                  }`}>
                    <div className="d-flex align-items-center">
                      {bulkUploadProgress.status === 'preparing' && <RefreshCw size={16} className="me-2 spin" />}
                      {bulkUploadProgress.status === 'uploading' && <Upload size={16} className="me-2" />}
                      {bulkUploadProgress.status === 'completed' && <CheckSquare size={16} className="me-2" />}
                      {bulkUploadProgress.status === 'error' && <Trash2 size={16} className="me-2" />}
                      <div className="flex-grow-1">
                        <strong>{bulkUploadProgress.message}</strong>
                        {bulkUploadProgress.current > 0 && (
                          <div className="progress mt-2" style={{ height: '6px' }}>
                            <div 
                              className="progress-bar" 
                              style={{ width: `${(bulkUploadProgress.current / bulkUploadProgress.total) * 100}%` }}
                            ></div>
                          </div>
                        )}
                        {bulkUploadProgress.status === 'completed' && (
                          <div className="mt-2">
                            <div>✅ Successful: {bulkUploadProgress.completed.length}</div>
                            {bulkUploadProgress.failed.length > 0 && (
                              <div>❌ Skipped: {bulkUploadProgress.failed.length}</div>
                            )}
                            {bulkUploadProgress.batch_id && (
                              <div>📦 Batch ID: {bulkUploadProgress.batch_id}</div>
                            )}
                            {bulkUploadProgress.batch_status && (
                              <div>⚡ Batch Status: {bulkUploadProgress.batch_status}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelBulkUpload}
                  disabled={vectorStoreUploading}
                >
                  {bulkUploadProgress?.status === 'completed' ? 'Close' : 'Cancel'}
                </button>
                {!bulkUploadProgress && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={confirmBulkUpload}
                    disabled={vectorStoreUploading || !selectedVectorStore}
                  >
                    <Cloud size={16} className="me-2" />
                    Start Bulk Upload
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;

// Add CSS for highlight effect and animations
const style = document.createElement('style');
style.textContent = `
  .highlight-pulse {
    animation: highlight-pulse 3s ease-in-out;
    border: 3px solid #007bff !important;
    box-shadow: 0 0 20px rgba(0, 123, 255, 0.5) !important;
  }
  
  @keyframes highlight-pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.02); }
    100% { transform: scale(1); }
  }
  
  .document-card.highlight-pulse {
    background-color: rgba(0, 123, 255, 0.1) !important;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);
